import { Link, useNavigate } from "react-router";
import { DashboardCard } from "../molecules/DashboardCard";
import { DueDateCell } from "../molecules/DueDateCell";
import { DocumentVersionStatus } from "~/config/types";
import { dateDiff } from "~/helpers/date";
import { useTranslation } from "react-i18next";
import NoResult from "../molecules/NoResult";
import SimpleDataTable, {
  type TableColumn,
} from "../molecules/SimpleDataTable";
import { CenterSpinner } from "../atoms/spinner";
import { Button } from "~/atoms/button";
import { useDocumentVersions } from "~/api/v2/useDocumentVersions";
import { type DocumentVersionSummary } from "~/api/types";
import { WorkflowProgress } from "~/molecules/WorkflowProgress";

const OngoingWorkflowsContainer = () => {
  const { t } = useTranslation();

  const tableColumns: TableColumn<DocumentVersionSummary>[] = [
    {
      header: t(($) => $.dashboard.column.title),
      compareFn: (a, b) => a.title.localeCompare(b.title),
      cell: (v) => (
        <div className="min-w-0">
          <span className="block truncate" title={v.title}>
            {v.title}
          </span>
        </div>
      ),
      cellClassName: "w-full max-w-0",
      tableHeadClassName: "w-full",
    },
    {
      header: t(($) => $.commons.workflow.progress),
      compareFn: (a, b) =>
        (a.completedSteps ?? 0) / (a.totalSteps ?? 1) -
        (b.completedSteps ?? 0) / (b.totalSteps ?? 1),
      cell: (wf) => (
        <WorkflowProgress
          completedSteps={wf.completedSteps ?? 0}
          totalSteps={wf.totalSteps ?? 0}
        />
      ),
      cellClassName: "min-w-32 whitespace-nowrap",
      tableHeadClassName: "min-w-32 whitespace-nowrap",
    },
    {
      header: t(($) => $.commons.document.reviewTo),
      defaultSort: true,
      compareFn: (a, b) => dateDiff(a.reviewTo, b.reviewTo),
      cell: (dv) => <DueDateCell dueDate={dv.reviewTo} />,
      cellClassName: "whitespace-nowrap",
      tableHeadClassName: "whitespace-nowrap",
    },
  ];
  const navigate = useNavigate();
  const { data: documentVersions, isLoading: isLoadingDocumentVersions } =
    useDocumentVersions({
      statuses: [DocumentVersionStatus.draft],
      includeWorkflowData: true,
    });

  const activeWorkflows = documentVersions?.filter(
    (dv) =>
      dv.isOngoing &&
      dv.status !== DocumentVersionStatus.archived &&
      dv.status !== DocumentVersionStatus.published,
  );

  const containerTitle = t(($) => $.dashboard.ongoing.workflows);

  if (isLoadingDocumentVersions) {
    return (
      <DashboardCard title={containerTitle} className="h-80">
        <CenterSpinner />
      </DashboardCard>
    );
  }

  if (activeWorkflows?.length === 0) {
    return (
      <DashboardCard title={containerTitle}>
        <NoResult className="h-full" />
      </DashboardCard>
    );
  }

  const seeAllButton = (
    <Button variant="link" asChild>
      <Link to="workflows?tab=ongoing">
        {t(($) => $.commons.buttons.seeAll)}
      </Link>
    </Button>
  );

  return (
    <DashboardCard title={containerTitle} button={seeAllButton}>
      <SimpleDataTable
        columns={tableColumns}
        data={activeWorkflows ?? []}
        onClickRow={(i) => navigate(`/documents/${i.document.id}?tab=workflow`)}
      />
    </DashboardCard>
  );
};

export default OngoingWorkflowsContainer;
